import { motion } from "framer-motion";
import { ShieldCheck, Settings, MapPin, CreditCard } from "lucide-react";

const ABOUT_IMG = "/about-cars.png";

const values = [
  { icon: ShieldCheck, label: "Sérieux et transparent" },
  { icon: Settings, label: "Solutions sur mesure" },
  { icon: MapPin, label: "Service de proximité" },
  { icon: CreditCard, label: "Paiement en plusieurs fois possible" },
];

export default function About() {
  return (
    <section id="apropos" className="relative py-24 sm:py-32 border-t border-white/10 bg-[#2E3336]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="relative"
        >
          <div className="clip-angle overflow-hidden border border-[#454C50]">
            <img
              src={ABOUT_IMG}
              alt="Rassemblement de youngtimers — Golf 2 GTI 16S Oettinger, Golf 1 GTI, Mercedes 190E Evo, BMW 325i E30 cabriolet, Golf Cabriolet Karmann, Audi 200 Quattro"
              className="w-full h-auto object-cover"
              data-testid="about-image"
            />
          </div>
          <div className="absolute -bottom-5 left-6 bg-[#6EB000] text-black px-6 py-3 clip-angle" data-testid="about-since-badge">
            <span className="font-display text-lg">Depuis 1998</span>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-10 lg:col-start-1"
          data-testid="about-caption"
        >
          <span className="block text-[10px] sm:text-[11px] uppercase tracking-widest text-white/35 mb-4" data-testid="about-car-names">
            De gauche à droite : Golf 2 GTI 16S Oettinger · Golf 1 GTI · Mercedes 190E 2.3 Evo · BMW 325i E30 Cabriolet · Golf Cabriolet Karmann · Audi 200 Quattro
          </span>
          <span className="font-script text-2xl sm:text-3xl text-[#6EB000] leading-snug block">
            Nicolas Bourillon, passionné d'automobile depuis 1998.
          </span>
          <span className="text-white/60 text-sm sm:text-base block mt-2 leading-relaxed">
            Voici quelques-unes de mes belles voitures de mes débuts, à l'époque où j'ai eu mon permis,
            au début des années 2000.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="lg:col-start-1"
        >
          <div className="clip-angle overflow-hidden border border-[#454C50]">
            <img
              src="/about-cars-2.png"
              alt="Rassemblement exposition auto — Golf 2 GTD kit G60, Peugeot 205 1.8 Turbo D Summer, Golf 3 1.4i, Audi 80 1.9 TD jantes BBS, Audi A4 B7 2.5 V6 TDI jantes BBS, Mercedes CLK 320 AMG, Porsche Cayenne V6 3.2L"
              className="w-full h-auto object-cover"
              loading="lazy"
              data-testid="about-image-2"
            />
          </div>
          <p
            className="text-[10px] sm:text-[11px] uppercase tracking-widest text-white/35 mt-4"
            data-testid="about-car-names-2"
          >
            De gauche à droite : Golf 2 GTD kit G60 · 205 1.8 Turbo D Summer · Golf 3 1.4i · Audi 80 1.9 TD · Audi A4 B7 2.5 V6 TDI · Mercedes CLK 320 AMG · Cayenne V6 3.2L
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
        >
          <p className="font-script text-2xl text-[#6EB000] mb-2">À propos</p>
          <h2 className="font-display text-4xl sm:text-6xl text-white leading-[0.95]">
            Nicolas <span className="text-[#6EB000]">Bourillon</span>
          </h2>
          <p className="text-white/60 mt-6 text-sm sm:text-base leading-relaxed">
            Passionné d'automobile depuis 1998, je mets mon expertise en électronique moteur
            au service de votre véhicule. Chez NBPerf, pas de discours commercial :
            une étude sérieuse, des informations claires et une intervention adaptée
            à votre voiture — sans promesse excessive.
          </p>
          <p className="font-script text-2xl text-[#F5A623] mt-5" data-testid="about-script-note">
            « Vous aider à voir si cette solution est vraiment intéressante pour vous. »
          </p>

          <div className="grid grid-cols-2 gap-4 mt-10">
            {values.map((v, i) => (
              <motion.div
                key={v.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="flex items-center gap-3 border border-[#454C50] p-4 hover:border-[#6EB000]/50 transition-colors duration-300"
                data-testid={`about-value-${i}`}
              >
                <v.icon className="w-6 h-6 text-[#6EB000] shrink-0" strokeWidth={1.6} />
                <span className="text-sm text-white/80">{v.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
